import { dictionaryApi } from './dictionaryClient';

export type SorakhariCombinationRow = {
  barna_id: number;
  bhed_id: number | null;
  order: number;
};

export type Sorakhari = {
  id: number;
  barnaId: number;
  text: string;
  combination: SorakhariCombinationRow[];
};

export type CreateSorakhariBody = {
  barnaId: number;
  text: string;
  combination: SorakhariCombinationRow[];
};

export type UpdateSorakhariBody = Partial<CreateSorakhariBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function toNum(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function toStr(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function normalizeCombination(raw: unknown): SorakhariCombinationRow | null {
  if (!isRecord(raw)) return null;
  const barnaId = toNum(raw.barna_id ?? raw.barnaId);
  const bhedId = toNum(raw.bhed_id ?? raw.bhedId);
  const order = toNum(raw.order);
  if (barnaId == null || order == null) return null;
  return { barna_id: barnaId, bhed_id: bhedId, order };
}

export function normalizeSorakhari(raw: unknown): Sorakhari | null {
  if (!isRecord(raw)) return null;
  const id = toNum(raw.id);
  const barnaId = toNum(raw.barnaId ?? raw.barna_id);
  if (id == null || barnaId == null) return null;
  return {
    id,
    barnaId,
    text: toStr(raw.text),
    combination: Array.isArray(raw.combination)
      ? raw.combination.map(normalizeCombination).filter((x): x is SorakhariCombinationRow => x != null)
      : [],
  };
}

function normalizeList(data: unknown): Sorakhari[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeSorakhari).filter((x): x is Sorakhari => x != null);
}

export async function listSorakhari(): Promise<Sorakhari[]> {
  const res = await dictionaryApi.get<unknown>('/vyanjan_sorakhari');
  return normalizeList(res.data);
}

export async function getSorakhari(id: number): Promise<Sorakhari> {
  const res = await dictionaryApi.get<unknown>(`/vyanjan_sorakhari/${id}`);
  const row = normalizeSorakhari(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sorakhari response');
  return row;
}

export async function createSorakhari(body: CreateSorakhariBody): Promise<Sorakhari> {
  const res = await dictionaryApi.post<unknown>('/vyanjan_sorakhari', body);
  const row = normalizeSorakhari(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sorakhari response');
  return row;
}

export async function updateSorakhari(id: number, body: UpdateSorakhariBody): Promise<Sorakhari> {
  const res = await dictionaryApi.patch<unknown>(`/vyanjan_sorakhari/${id}`, body);
  const row = normalizeSorakhari(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid sorakhari response');
  return row;
}

export async function deleteSorakhari(id: number): Promise<void> {
  await dictionaryApi.delete(`/vyanjan_sorakhari/${id}`);
}
